/**
 * <plp-banner-grid> wraps the product grid <ul id="product-grid">.
 *
 * Desktop keeps the Liquid row-based zigzag banner placement rendered
 * server-side. Mobile uses a flat rule instead: banner 1 after product 2,
 * then every 10th product after that (2, 12, 22, 32, ...). This component
 * moves the already-rendered banner <li> elements to those flat positions
 * when the viewport is mobile, and restores them to their original
 * (desktop) position when the viewport is desktop.
 *
 * Repositions on connect, on resize/orientationchange (debounced), and
 * must be re-invoked via `.placeBanners()` after each AJAX infinite-scroll
 * append (see main-collection-product-grid.liquid callback).
 */
class PlpBannerGrid extends HTMLElement {
  static MOBILE_BREAKPOINT = 989;
  static FIRST_BANNER_AT = 2;
  static BANNER_INTERVAL = 10;

  constructor() {
    super();
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.placeBanners();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    clearTimeout(this.resizeTimer);
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.placeBanners(), 150);
  }

  isMobile() {
    return window.innerWidth <= this.constructor.MOBILE_BREAKPOINT;
  }

  placeBanners() {
    if (this.isMobile()) {
      this.placeBannersMobile();
    } else {
      this.restoreBannersDesktop();
    }
  }

  placeBannersMobile() {
    const banners = Array.from(this.querySelectorAll('.plp-banner'));
    if (!banners.length) return;

    banners.forEach((banner, i) => {
      if (!banner.parentNode) return;

      // Stash original position once so we can restore it on desktop.
      if (!banner.hasAttribute('data-mobile-placed')) {
        const anchor = document.createComment('plp-banner-origin-' + i);
        banner.parentNode.insertBefore(anchor, banner);
        banner._plpOriginAnchor = anchor;
      }

      const targetIndex = this.constructor.FIRST_BANNER_AT + i * this.constructor.BANNER_INTERVAL;
      const products = Array.from(this.querySelectorAll('.grid__item:not(.plp-banner)'));
      const refProduct = products[targetIndex - 1];

      if (refProduct && refProduct.nextSibling !== banner) {
        refProduct.parentNode.insertBefore(banner, refProduct.nextSibling);
      } else if (!refProduct && banner.parentNode) {
        // Not enough products loaded yet for this banner's slot; append at end.
        this.appendChild(banner);
      }

      banner.setAttribute('data-mobile-placed', 'true');
    });
  }

  restoreBannersDesktop() {
    const banners = Array.from(this.querySelectorAll('.plp-banner[data-mobile-placed]'));
    banners.forEach((banner) => {
      const anchor = banner._plpOriginAnchor;
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(banner, anchor.nextSibling);
        anchor.parentNode.removeChild(anchor);
      }
      banner._plpOriginAnchor = null;
      banner.removeAttribute('data-mobile-placed');
    });
  }
}

customElements.define('plp-banner-grid', PlpBannerGrid);
